<script setup lang="ts">
import { computed, ref } from 'vue';
import type { TaskItem } from '@Types/dashboard.types';
import type { FraudEducationCompletionTaskData } from '@Types/completionTaskData.types';
import CloudflarePlayer from '@Components/CloudflarePlayer.vue';
import { completeTaskApi } from '@Api/dashboard.api';
import { useDashboardStore } from '@Stores/dashboard.store';
import { useTaskSheet } from '@Use/useTaskSheet';

const props = defineProps<{
    task: TaskItem;
}>();

const isCompleted = computed(() => props.task.status === 'completed');

const data = computed<FraudEducationCompletionTaskData>(() =>
    (props.task.taskData ?? {}) as FraudEducationCompletionTaskData,
);
const videoUrl = computed(() => data.value.video_url ?? '');

// Role-specific tips come from the BFF on taskData.items. Same content for
// active and completed — the BFF is the source of truth for which bullets
// (Buyer/Seller/Agent variants) render. No client-side fallback copy.
const tips = computed(() => data.value.items ?? []);

// Active-state local state (no global composable — each mount starts fresh).
const hasPlayed = ref(false);
const isChecked = ref(false);
const isCompleting = ref(false);
const error = ref<string | null>(null);

const isCheckboxEnabled = computed(() => hasPlayed.value && !isCompleted.value);
const isContinueEnabled = computed(
    () => isChecked.value && !isCompleted.value && !isCompleting.value,
);

const onVideoPlay = () => {
    hasPlayed.value = true;
};

const { closeSheet } = useTaskSheet();
const dashboardStore = useDashboardStore();

const handleContinue = async () => {
    if (!isContinueEnabled.value) return;
    isCompleting.value = true;
    error.value = null;
    try {
        await completeTaskApi(props.task.id);
        closeSheet();
        await dashboardStore.loadDashboard();
    } catch {
        error.value = 'Failed to complete task. Please try again.';
    } finally {
        isCompleting.value = false;
    }
};

</script>

<template>
    <div class="fev" :class="{ 'fev--completed': isCompleted }">
        <div class="fev__video">
            <div class="fev__video-frame">
                <CloudflarePlayer
                    v-if="videoUrl"
                    :video-url="videoUrl"
                    @play="onVideoPlay"
                />
                <div v-else class="fev__video-placeholder">
                    Video unavailable
                </div>
            </div>
        </div>

        <div class="fev__body">
            <h2 class="fev__heading">Fraud Education</h2>

            <ul v-if="tips.length" class="fev__tips">
                <li v-for="tip in tips" :key="tip.text" class="fev__tip">
                    <span class="fev__tip-icon">
                        <app-icon :icon="tip.icon" />
                    </span>
                    <span class="fev__tip-text">{{ tip.text }}</span>
                </li>
            </ul>

            <template v-if="!isCompleted">
                <app-checkbox
                    class="fev__checkbox"
                    v-model="isChecked"
                    :disabled="!isCheckboxEnabled"
                >
                    I have watched and understand this content
                </app-checkbox>

                <app-button
                    expanded
                    size="large"
                    type="primary"
                    :is-loading="isCompleting"
                    :disabled="!isContinueEnabled"
                    @onClick="handleContinue"
                >
                    Continue
                </app-button>

                <p v-if="error" class="fev__error">{{ error }}</p>
            </template>

            <section v-else class="fev__report">
                <p class="fev__report-title">Spotted something suspicious?</p>
                <p class="fev__report-body">
                    Report it immediately at
                    <a
                        href="https://reportafraud.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="fev__report-link"
                    >reportafraud.org</a>.
                </p>
                <p class="fev__report-body">
                    If you need to speak with someone urgently, call
                    <a href="tel:6162026612" class="fev__report-link">(616) 202-6612</a>.
                </p>
            </section>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.fev {
    display: flex;
    flex-direction: column;

    &__video {
        background: $gray-lightest;
        padding: 44px $portal-section-px 24px;

        @media (min-width: 768px) {
            padding: 32px 32px 24px;
            border-radius: $portal-modal-radius;
        }
    }

    &__video-frame {
        position: relative;
        width: 100%;
        aspect-ratio: 16 / 9;
        border-radius: $portal-card-radius;
        overflow: hidden;
        background: #000; // video letterbox — intentional pure black, not a theme surface
    }

    &__video-placeholder {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $white;
        font-size: $text-portal-caption;
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 24px $portal-section-px 0;

        @media (min-width: 768px) {
            padding: 24px 32px 0;
        }
    }

    // DS-5361: the completed view keeps the Zendesk launcher, which is fixed
    // bottom-left and paints over the end of the content. The scroll container
    // (.bsm__content) zeroes its padding in fullscreen mode, so with no bottom
    // clearance the last block can never be scrolled clear of the launcher.
    // 96px covers the ~56px bubble plus its 16px offset with room to spare.
    //
    // Mobile only: at ≥768px .tcs__column already reserves 96px, and the
    // launcher is far from the centered content column anyway. The active view
    // is excluded — it hides the launcher, so clearance there is dead space.
    &--completed &__body {
        padding-bottom: 96px;

        @media (min-width: 768px) {
            padding-bottom: 0;
        }
    }

    &__heading {
        font-size: $text-portal-h4;
        font-weight: 700;
        color: $portal-text;
        line-height: 1.3;
        margin: 0;
    }

    &__tips {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    &__tip {
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    // DS-2861: inline icons in the brand color (no circular badge). Removes
    // the dark-on-dark contrast issue from the legacy modal entirely.
    // AppIcon renders `<i :class>` font glyphs — size is via font-size on
    // the wrapper, not width/height on an svg.
    &__tip-icon {
        flex-shrink: 0;
        color: $theme-primary-dark;
        font-size: 22px;
        line-height: 1;
        margin-top: 3px;
    }

    &__tip-text {
        font-size: $text-portal-caption;
        color: $portal-text;
        line-height: 1.5;
    }

    &__checkbox {
        font-size: $text-portal-caption;
        color: $portal-text-mid;
        line-height: 1.5;
    }

    &__error {
        font-size: $text-portal-small;
        color: $red;
        margin: 0;
    }

    &__report {
        border: 1px solid $portal-border;
        border-radius: $portal-card-radius;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__report-title {
        font-size: $text-portal-caption;
        font-weight: 700;
        color: $portal-text;
        margin: 0;
    }

    &__report-body {
        font-size: $text-portal-small;
        color: $portal-text;
        line-height: 1.5;
        margin: 0;
    }

    &__report-link {
        color: var(--ch-primary, #156FBE);
        text-decoration: underline;
        font-weight: 700;
    }
}
</style>
